import { THEME } from '@common/constants/theme.constant';
import { Field, IFieldProps } from '@common/general-ui/field/field.component';
import { Readonly } from '@common/general-ui/readonly.component';
import { SerializedStyles } from '@emotion/react';
import dayjs, { Dayjs } from 'dayjs';
import React, { useCallback, useMemo } from 'react';
import { css } from 'twin.macro';
import { AntTimePickerWithDayjs } from './ant-time-picker.component';

export type ITimePickerChangeHandler = (props: { name: string; value: Date | undefined; valueStr: string }) => void;

export interface ITimePickerProps<TName = string> extends IFieldProps<Date | undefined, TName> {
    className?: string;
    onChange?: ITimePickerChangeHandler;
    onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
}

export const TimePicker = (props: ITimePickerProps): JSX.Element => {
    const { name, onChange, onBlur, value } = props;

    const { className, ...fieldProps } = props;

    const handleChange = useCallback(
        (val: Dayjs | null, valStr: string) => {
            onChange && onChange({ name, value: val?.toDate(), valueStr: valStr });
        },
        [name, onChange],
    );
    const dayjsValue = useMemo(() => (value ? dayjs(value) : value), [value]);
    return (
        <Field {...fieldProps} data-testid={`field-${name}`}>
            <div tw="relative">
                {props.readOnly && <Readonly />}
                <AntTimePickerWithDayjs
                    tabIndex={props.readOnly ? -1 : 0}
                    css={styles.input(!!props.readOnly)}
                    inputReadOnly={props.readOnly}
                    format="HH:mm"
                    value={dayjsValue}
                    name={name}
                    panelRender={(e): JSX.Element => <div css={styles.popup}>{e}</div>}
                    onChange={handleChange}
                    onBlur={onBlur}
                />
            </div>
        </Field>
    );
};

const styles = {
    input: (readOnly: boolean): (SerializedStyles | boolean)[] => [
        css`
            display: block;
            border-color: ${THEME.GRAY_300};
            border-radius: 4px;
            padding: 12px 16px;
        `,
        readOnly &&
            css`
                border-color: ${THEME.READONLY_COLOR};
            `,
    ],
    popup: css`
        && .ant-picker-time-panel-cell-selected {
            .ant-picker-time-panel-cell-inner {
                background-color: ${THEME.GREEN};
                color: white;
            }
        }
    `,
};
